import { TFunction } from "i18next";

/**
 * Translates time strings like "Just now", "5 minutes ago", etc.
 * @param timeStr - The time string to translate (e.g., "Just now", "2 hours ago")
 * @param t - The translation function from react-i18next
 * @returns The translated time string
 */
export function translateTime(
  timeStr: string,
  t: TFunction
): string {
  if (timeStr === "Just now") {
    return t("dashboard.recentActivity.time.justNow");
  }
  
  if (timeStr.includes("minute")) {
    const minutesMatch = timeStr.match(/(\d+)\s+minute/);
    if (minutesMatch) {
      const minutes = parseInt(minutesMatch[1]);
      return t("dashboard.recentActivity.time.minutesAgo", { count: minutes });
    }
  }
  
  if (timeStr.includes("hour")) {
    const hoursMatch = timeStr.match(/(\d+)\s+hour/);
    if (hoursMatch) {
      const hours = parseInt(hoursMatch[1]);
      return t("dashboard.recentActivity.time.hoursAgo", { count: hours });
    }
  }
  
  if (timeStr.includes("day")) {
    const daysMatch = timeStr.match(/(\d+)\s+day/);
    if (daysMatch) {
      const days = parseInt(daysMatch[1]);
      return t("dashboard.recentActivity.time.daysAgo", { count: days });
    }
  }
  
  // Return original if no match
  return timeStr;
}


// Older APIs append an English "(Booked by: Name)" to the description.
const LEGACY_BOOKED_BY = /\s*\(Booked by:\s*(.+)\)\s*$/;

/**
 * MED-1648: an activity's description with the booker label in the user's language. The API
 * sends the booker as `bookedByName`; older APIs put it in English in the description.
 */
export function describeActivity(
  activity: { description: string; bookedByName?: string | null },
  t: TFunction
): string {
  const legacy = activity.description?.match(LEGACY_BOOKED_BY);
  const description = legacy
    ? activity.description.replace(LEGACY_BOOKED_BY, "")
    : activity.description;
  const bookedBy = activity.bookedByName || legacy?.[1];
  return bookedBy
    ? `${description} (${t("dashboard.recentActivity.bookedBy", { name: bookedBy })})`
    : description;
}
